import React from 'react';
import { Button, Space, Typography, Popover, List, Badge } from 'antd';
import {
  UndoOutlined,
  RedoOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

/**
 * Translate raw backend validation messages into plain language
 * that non-technical tenants can understand.
 */
function humanizeMessage(raw = '') {
  const msg = raw.toLowerCase();

  // ── Warnings ────────────────────────────────────────────────────────────────
  if (msg.includes('no outgoing edges') || msg.includes('no outgoing edge'))
    return 'This step has no next step connected. Connect it to the next message or action in your flow.';

  if (msg.includes('cycle') || msg.includes('loop without') || msg.includes('loop back'))
    return 'This step is part of a loop that never ends. Make sure the flow eventually reaches an End step.';

  if (msg.includes('unreachable') || msg.includes('not reachable'))
    return 'This step can never be reached. Check that something connects to it, or remove it.';

  if (msg.includes('missing keyword') || msg.includes('no keywords'))
    return 'The starting keyword is empty. Add at least one keyword so the flow knows when to trigger.';

  if (msg.includes('empty body') || msg.includes('no body') || msg.includes('message body is empty'))
    return 'The message text is empty. Write the message you want to send to the customer.';

  if (msg.includes('no template') || msg.includes('template not selected'))
    return 'No template is selected for this step. Choose a WhatsApp template to continue.';

  if (msg.includes('var') && msg.includes('not set'))
    return 'No variable name is set for this step. Give it a name so the answer can be saved and used later.';

  if (msg.includes('invalid url') || msg.includes('url is empty') || msg.includes('missing url'))
    return 'The API URL is missing or invalid. Enter a valid web address for the API call.';

  if (msg.includes('no route') || msg.includes('no branches'))
    return "No routing branches are set up. Add at least one branch to tell the flow where to go based on the customer's choice.";

  if (msg.includes('condition') && (msg.includes('empty') || msg.includes('missing')))
    return 'The condition is incomplete. Set both the variable and the value to check.';

  if (msg.includes('duplicate') && msg.includes('node'))
    return 'There are duplicate steps in the flow. Check for steps that appear more than once.';

  if (msg.includes('flow.end') || msg.includes('no end node') || msg.includes('missing end'))
    return 'The flow has no End step. Add an "End: Clear session" step so the conversation can finish properly.';

  // ── Errors ───────────────────────────────────────────────────────────────────
  if (msg.includes('trigger') && msg.includes('missing'))
    return 'The flow has no trigger. Add a keyword or default trigger at the start.';

  if (msg.includes('invalid json') || msg.includes('parse error'))
    return 'The flow data contains invalid content. Try re-saving or re-importing the flow.';

  if (msg.includes('node not found') || msg.includes('missing node'))
    return 'A step is referenced in the flow but no longer exists. Remove any broken connections.';

  // Fallback — clean up raw snake_case / punctuation but still show something readable
  return raw
    .replace(/_/g, ' ')
    .replace(/\b(node)\b/gi, 'step')
    .replace(/\b(edge)\b/gi, 'connection')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

const FlowEditorFooter = ({
  warningCount = 0,
  lastSavedAt,
  onUndo,
  onRedo,
  showZoom,
  validation = { valid: true, errors: [], warnings: [] },
  onSelectNode,
}) => {
  const savedLabel = lastSavedAt ? 'Saved' : '';
  const errorCount = validation?.errors?.length || 0;
  const warnings = validation?.warnings || [];
  const errors = validation?.errors || [];

  const popoverContent = (
    <div style={{ width: 360, maxHeight: 340, overflowY: 'auto' }}>
      {errors.length === 0 && warnings.length === 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Your flow looks good — no issues found.
        </Text>
      )}

      {errors.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Text strong type="danger" style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
            Issues to fix before publishing ({errors.length})
          </Text>
          <List
            size="small"
            dataSource={errors}
            renderItem={(e) => {
              const nid = e.nodeId || e.node_id;
              return (
                <List.Item
                  style={{
                    padding: '8px 0',
                    fontSize: 12,
                    cursor: nid ? 'pointer' : 'default',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                  onClick={() => nid && onSelectNode?.(nid)}
                >
                  <Space align="start">
                    <CloseCircleOutlined style={{ color: '#ff4d4f', marginTop: 3 }} />
                    <div>
                      <div style={{ fontWeight: 500 }}>
                        {humanizeMessage(e.message || JSON.stringify(e))}
                      </div>
                      {nid && (
                        <Text type="secondary" style={{ fontSize: 10, color: '#1890ff' }}>
                          Click to jump to this step
                        </Text>
                      )}
                    </div>
                  </Space>
                </List.Item>
              );
            }}
          />
        </div>
      )}

      {warnings.length > 0 && (
        <div>
          <Text strong style={{ color: '#d48806', display: 'block', marginBottom: 6, fontSize: 13 }}>
            Things to review ({warnings.length})
          </Text>
          <List
            size="small"
            dataSource={warnings}
            renderItem={(w) => {
              const nid = w.nodeId || w.node_id;
              return (
                <List.Item
                  style={{
                    padding: '8px 0',
                    fontSize: 12,
                    cursor: nid ? 'pointer' : 'default',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                  onClick={() => nid && onSelectNode?.(nid)}
                >
                  <Space align="start">
                    <WarningOutlined style={{ color: '#faad14', marginTop: 3 }} />
                    <div>
                      <div style={{ fontWeight: 500 }}>
                        {humanizeMessage(w.message || JSON.stringify(w))}
                      </div>
                      {nid && (
                        <Text type="secondary" style={{ fontSize: 10, color: '#1890ff' }}>
                          Click to jump to this step
                        </Text>
                      )}
                    </div>
                  </Space>
                </List.Item>
              );
            }}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="flow-builder-footer">
      <Space>
        <Button type="text" icon={<UndoOutlined />} onClick={onUndo}>
          Undo
        </Button>
        <Button type="text" icon={<RedoOutlined />} onClick={onRedo}>
          Redo
        </Button>
      </Space>

      <Space size="large">
        <Popover
          content={popoverContent}
          title="Flow check results"
          trigger="click"
          placement="topRight"
        >
          <Button type="text" size="small" style={{ padding: '0 8px', height: 'auto' }}>
            <Space>
              {errorCount > 0 && (
                <>
                  <Badge count={errorCount} size="small" style={{ marginRight: 8 }} />
                  <Text type="danger" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 500 }}>
                    <CloseCircleOutlined /> Issues
                  </Text>
                </>
              )}
              {warningCount > 0 && (
                <>
                  <Badge count={warningCount} size="small" style={{ marginRight: 8, backgroundColor: '#faad14' }} />
                  <Text style={{ color: '#d48806', display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 500 }}>
                    <WarningOutlined /> Warnings
                  </Text>
                </>
              )}
              {errorCount === 0 && warningCount === 0 && (
                <Text type="success" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 500 }}>
                  <CheckCircleOutlined /> All good
                </Text>
              )}
            </Space>
          </Button>
        </Popover>

        {savedLabel && (
          <Text type="success" style={{ fontSize: 12 }}>
            <CheckCircleOutlined /> {savedLabel}
          </Text>
        )}
      </Space>

      {showZoom && (
        <Space>
          <Button size="small">−</Button>
          <Button size="small">+</Button>
        </Space>
      )}
    </div>
  );
};

export default FlowEditorFooter;
